import { useCallback, useEffect, useState } from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import CamembertMarque from "@/components/CamembertMarque";

const ETATS = {
  rouge: { label: "PANNE", fond: "bg-red-600", bord: "border-red-500", point: "bg-white" },
  orange: { label: "À SURVEILLER", fond: "bg-amber-500", bord: "border-amber-400", point: "bg-white" },
  vert: { label: "OK", fond: "bg-emerald-600", bord: "border-emerald-500", point: "bg-white" },
  silencieux: { label: "EN VEILLE", fond: "bg-slate-800", bord: "border-slate-700", point: "bg-slate-400" },
};

export default function AdminMural() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [heure, setHeure] = useState(new Date());
  const [plein, setPlein] = useState(false);

  const load = useCallback(() => {
    api
      .get("/admin/supervision")
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const rafraichir = setInterval(load, 15000);
    const horloge = setInterval(() => setHeure(new Date()), 1000);
    return () => {
      clearInterval(rafraichir);
      clearInterval(horloge);
    };
  }, [load]);

  const basculerPlein = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      setPlein(false);
    } else {
      await document.documentElement.requestFullscreen?.();
      setPlein(true);
    }
  };

  const sites = data?.sites || [];
  const r = data?.resume;
  const alerte = (r?.rouge || 0) > 0;
  const vigilance = !alerte && (r?.orange || 0) > 0;
  const fondGeneral = alerte ? "bg-red-600" : vigilance ? "bg-amber-500" : "bg-emerald-600";
  const texteGeneral = alerte
    ? `${r.rouge} PANNE${r.rouge > 1 ? "S" : ""}`
    : vigilance
      ? `${r.orange} À SURVEILLER`
      : "TOUT EST OK";

  return (
    <div className="min-h-screen bg-[#0B1120] text-white p-6 sm:p-10 select-none" data-testid="admin-mural">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-black tracking-tighter">BKPROD</span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">Supervision atelier</span>
          </div>
          <div className="text-sm text-white/50 mt-1">
            {data?.ouvert ? "Bureau ouvert" : "Bureau fermé — l'app continue de veiller"} · actualisé toutes les 15 s
          </div>
        </div>

        <div className="flex items-start gap-6">
          <div className="text-right">
            <div className="font-mono-bk text-4xl sm:text-5xl font-bold tabular-nums" data-testid="mural-horloge">
              {heure.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="text-xs text-white/40 capitalize">
              {heure.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button data-testid="mural-fullscreen" onClick={basculerPlein} className="text-white/40 hover:text-white transition-colors">
              {plein ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
            <button data-testid="mural-close" onClick={() => navigate("/admin/supervision")} className="text-white/40 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {r && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <div className={`rounded-2xl p-5 ${fondGeneral}`} data-testid="mural-kpi-etat">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">État général</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-1">{texteGeneral}</div>
          </div>
          <div className="rounded-2xl p-5 bg-white/5 border border-white/10" data-testid="mural-kpi-sites">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Sites supervisés</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-1">{r.sites}</div>
          </div>
          <div className="rounded-2xl p-5 bg-white/5 border border-white/10" data-testid="mural-kpi-orange">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">À surveiller</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-1">{r.orange}</div>
          </div>
          <div className="rounded-2xl p-5 bg-white/5 border border-white/10" data-testid="mural-kpi-alertes">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Alertes 24 h</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-1">{r.alertes_24h}</div>
          </div>
        </div>
      )}

      {data?.charge_du_jour && (
        <div
          className={`rounded-2xl border p-5 mt-4 ${data.charge_du_jour.journee_pleine ? "border-amber-400 bg-amber-500/15" : "border-white/10 bg-white/5"}`}
          data-testid="mural-charge"
        >
          <div className="flex flex-wrap items-end gap-x-10 gap-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">À traiter aujourd'hui</div>
              <div className="font-display text-3xl font-black tracking-tighter">{data.charge_du_jour.a_traiter_aujourdhui}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Bloquants</div>
              <div className="font-display text-3xl font-black tracking-tighter text-red-400">{data.charge_du_jour.bloquants}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Tickets ouverts</div>
              <div className="font-display text-3xl font-black tracking-tighter">{data.charge_du_jour.tickets_ouverts}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Restant aujourd'hui</div>
              <div className="font-display text-3xl font-black tracking-tighter">{data.charge_du_jour.heures_restantes} h</div>
            </div>
            {data.charge_du_jour.journee_pleine && (
              <div className="text-sm font-semibold text-amber-300" data-testid="mural-journee-pleine">
                Journée pleine — à replanifier
              </div>
            )}
          </div>
        </div>
      )}

      {data?.marques?.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="mural-camemberts">
          {data.marques.map((m) => (
            <CamembertMarque key={m.cle} marque={m} sombre />
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        {sites.map((s) => {
          const e = ETATS[s.etat] || ETATS.silencieux;
          return (
            <div
              key={s.id}
              data-testid={`mural-site-${s.id}`}
              className={`rounded-2xl border p-5 ${e.bord} ${s.etat === "silencieux" ? "bg-white/5" : e.fond}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/70">{e.label}</span>
                <span className={`h-3 w-3 rounded-full ${e.point} ${s.etat === "rouge" ? "animate-pulse" : ""}`} />
              </div>
              <div className="font-display text-xl font-bold tracking-tight mt-2 leading-tight">{s.client}</div>
              <div className="text-sm text-white/70">{s.site_nom}</div>

              {s.tickets_ouverts.length > 0 ? (
                <div className="mt-3 space-y-1">
                  {s.tickets_ouverts.slice(0, 3).map((t) => (
                    <div key={t.reference} className="text-sm text-white/90">
                      <span className="font-mono-bk">{t.reference}</span> · {t.domaine}
                      {t.materiel ? ` · ${t.materiel}` : ""}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 text-sm text-white/50">
                  {s.alertes_24h > 0 ? `${s.alertes_24h} alerte(s) sur 24 h` : "Aucune alerte en cours"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sites.length === 0 && (
        <p className="text-white/50 mt-10" data-testid="mural-empty">
          Aucun site supervisé pour l'instant. Créez une clé d'alerte dans « Alertes équipements ».
        </p>
      )}
    </div>
  );
}
